import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, ArbeitsplatzAuswahl } from '../api';
import type { Rahmen } from './Projektrahmen';
import { IconRaumLesen, IconRaumSchreiben } from '../Icons';
import { ConfirmDialog, EmptyState, Feld } from '../muster';
import { useModellSchreibrecht } from '../rechte';

/**
 * Aus dem Modell lesen und ins Modell zurückschreiben.
 *
 * Steht auf jeder Fachseite im Kopf, nicht in der Modellzeile der Übersicht: Wer
 * im Raumbuch arbeitet und dort etwas ändert, will es von dort aus
 * zurückschreiben — und nicht einen Reiter zurück und die richtige Zeile suchen.
 *
 * Das Quellmodell kommt aus der Modellzuordnung. Wer hier klickt, wählt keines
 * aus; es gibt für jeden Fachbereich genau eine markierte Quelle. Sind mehrere
 * markiert, nimmt die Seite die erste und sagt es.
 *
 * Die Aufnahme ist immer ein Abgleich: Wiedergefundenes wird aktualisiert, Neues
 * angelegt, Verschwundenes gekennzeichnet. Gepflegte Werte bleiben unberührt.
 *
 * Den Knopf „Ins Modell schreiben" (und seine Rückfrage) sieht nur, wer ins
 * Modell schreiben darf (TD-WR1): Seit S12 weist der Server eine Rückgabe ohne
 * ProjectAdmin oder SystemAdmin mit 403 ab. Ausgeblendet wird hier, gesperrt
 * bleibt im Server.
 */
export function Fachaktionen({
  api, rolleKey, leseArt, schreibArt, leseText, schreibText,
  zusatzArten, children,
}: {
  api: ApiClient;
  /** Schlüssel des Fachbereichs, etwa raumbuch oder lueftung. */
  rolleKey: string;
  /** ExportKind der Aufnahme: 10 Raum, 20 Tür, 30 Lüftung. */
  leseArt: number;
  /**
   * ExportKind der Rückgabe. Fehlt sie, entfällt der Knopf - ebenso für alle,
   * die nicht ins Modell schreiben dürfen (TD-WR1, `useModellSchreibrecht`).
   *
   * Die Gebäudehülle hat bewusst keine: Sie gehört dem Architekten. Ein
   * Knopf, der nichts tut, wäre schlimmer als keiner — er stünde da wie ein
   * Angebot.
   */
  schreibArt?: number;
  leseText?: string;
  schreibText?: string;
  /**
   * Weitere Leseläufe desselben Fachbereichs.
   *
   * Für Heizung das Rohrnetz: eine eigene Auftragsart, weil sie das
   * Fünfzehnfache der Bauteile liest — 40.000 Elemente gegen 2.750. Wer nur
   * Armaturenwerte auffrischt, soll das nicht versehentlich auslösen.
   *
   * Als Eigenschaft und nicht in children, weil die Knöpfe dort keinen Zugriff
   * auf die Auftragsvergabe haben: Das Quellmodell und die Profilfassung
   * werden hier aufgelöst, und zweimal aufgelöst hieße zwei Wahrheiten
   * darüber, in welches Modell geschrieben wird.
   */
  zusatzArten?: { art: number; text: string; titel?: string }[];
  /** Weitere Knöpfe rechts, etwa Excel oder Vorlagen. */
  children?: React.ReactNode;
}) {
  const { projekt, bereiche } = useOutletContext<Rahmen>();
  // TD-WR1: false, solange die Antwort fehlt oder scheitert - im Zweifel kein Schreibknopf.
  const darfSchreiben = useModellSchreibrecht(api);
  const schreibKnopf = schreibArt !== undefined && darfSchreiben;

  const [profilId, setProfilId] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<number | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fragt, setFragt] = useState(false);
  const [arbeitsplaetze, setArbeitsplaetze] = useState<ArbeitsplatzAuswahl[]>([]);
  const [arbeitsplatzId, setArbeitsplatzId] = useState('');

  const quellen = projekt.modelle.filter(
    (m) => m.isActive && (m.rollen ?? []).includes(rolleKey));

  const modell = quellen[0];
  const bereich = bereiche.find((b) => b.roleKey === rolleKey);

  useEffect(() => {
    if (!modell) return;
    api.profile(projekt.id, modell.revitVersion)
      .then((p) => setProfilId(p.flatMap((x) => x.versions)[0]?.id ?? null))
      .catch(() => setProfilId(null));
  }, [api, projekt.id, modell?.id, modell?.revitVersion]);

  // Ohne Auswahl entsteht wie bisher ein Maschinen-Teilauftrag -- die Liste
  // ist eine Zusatzoption, kein Zwang. Bleibt sie leer (kein Arbeitsplatz
  // gemeldet oder alle gesperrt), verschwindet einfach das Auswahlfeld.
  useEffect(() => {
    api.arbeitsplaetzeVerfuegbar().then(setArbeitsplaetze).catch(() => setArbeitsplaetze([]));
  }, [api]);

  const beauftragen = async (art: number, name: string) => {
    if (!modell || !profilId) return;

    setLaeuft(art);
    setMeldung(null);
    try {
      const auftrag = await api.auftragAnlegen({
        projectId: projekt.id,
        exportProfileVersionId: profilId,
        modelIds: [modell.id],
        kinds: [art],
        arbeitsplatzId: arbeitsplatzId || undefined,
      });
      const ziel = arbeitsplaetze.find((a) => a.id === arbeitsplatzId);
      setMeldung(`${name}: Auftrag ${auftrag.jobNumber} angelegt`
               + (ziel ? ` für Arbeitsplatz ${ziel.name}` : '')
               + '. Das Ergebnis erscheint hier, sobald er durchgelaufen ist.');
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  };

  const bereit = modell !== undefined && profilId !== null;

  return (
    <div className="karte">
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          className="betont"
          disabled={!bereit || laeuft !== null}
          title={modell
            ? `Aus ${modell.name} lesen und mit dem Bestand abgleichen`
            : 'Kein Modell als Quelle markiert'}
          onClick={() => beauftragen(leseArt, leseText ?? 'Aus Modell lesen')}
        >
          <IconRaumLesen size={16} />{leseText ?? 'Aus Modell lesen'}
        </button>

        {schreibKnopf && (
          <button
            disabled={!bereit || laeuft !== null}
            title={modell
              ? `Gepflegte Werte nach ${modell.name} zurückschreiben`
              : 'Kein Modell als Quelle markiert'}
            onClick={() => setFragt(true)}
            style={{ marginRight: 8 }}
          >
            <IconRaumSchreiben size={16} />{schreibText ?? 'Ins Modell schreiben'}
          </button>
        )}

        {(zusatzArten ?? []).map((z) => (
          <button
            key={z.art}
            disabled={!bereit || laeuft !== null}
            title={modell
              ? (z.titel ?? `Aus ${modell.name} lesen`)
              : 'Kein Modell als Quelle markiert'}
            onClick={() => beauftragen(z.art, z.text)}
          >
            <IconRaumLesen size={16} />{z.text}
          </button>
        ))}

        {children}

        {arbeitsplaetze.length > 0 && (
          <div style={{ marginLeft: 'auto', minWidth: 240 }}>
            <Feld
              art="auswahl"
              beschriftung="Ausführen auf"
              wert={arbeitsplatzId}
              deaktiviert={laeuft !== null}
              hinweis="Statt einer Verarbeitungsmaschine an einem bestimmten Arbeitsplatz."
              optionen={[
                { wert: '', beschriftung: 'Verarbeitungsmaschine' },
                ...arbeitsplaetze.map((a) => ({
                  wert: a.id,
                  beschriftung: `${a.name}${a.benutzer ? ` (${a.benutzer})` : ''}`
                    + `${!a.erreichbar ? ' – nicht erreichbar' : ''}`,
                })),
              ]}
              beiAenderung={(w) => setArbeitsplatzId(w as string)}
            />
          </div>
        )}

        <span className="schwach" style={{ marginLeft: arbeitsplaetze.length > 0 ? 8 : 'auto', fontSize: 12 }}>
          {modell ? `Quelle: ${modell.name}` : 'kein Quellmodell'}
        </span>
      </div>

      {!modell && (
        <EmptyState
          klein
          titel={`Kein Modell ist als Quelle für „${bereich?.bezeichnung ?? rolleKey}" `
            + 'markiert.'}
          erklaerung={'Unter „Einstellungen" in der Modellzuordnung den Haken setzen — '
            + 'erst dann lässt sich eine Aufnahme beauftragen.'}
        />
      )}

      {modell && profilId === null && (
        <EmptyState
          klein
          titel={`Kein freigegebenes Exportprofil für Revit ${modell.revitVersion}.`}
          erklaerung={'Ohne Profil lässt sich kein Auftrag anlegen — unter „Profile" '
            + 'eine Fassung freigeben.'}
        />
      )}

      {meldung && <div className="hinweis" style={{ marginTop: 12 }}>{meldung}</div>}

      {quellen.length > 1 && (
        <p className="schwach" style={{ marginBottom: 0, marginTop: 10, fontSize: 12 }}>
          Mehrere Quellmodelle markiert. Gelesen und geschrieben wird in{' '}
          {modell.name}.
        </p>
      )}

      {modell && schreibKnopf && schreibArt !== undefined && (
        <Rueckfrage
          offen={fragt}
          modell={modell.name}
          bereich={bereich?.bezeichnung ?? rolleKey}
          abbrechen={() => setFragt(false)}
          bestaetigen={() => {
            setFragt(false);
            beauftragen(schreibArt, schreibText ?? 'Ins Modell schreiben');
          }}
        />
      )}
    </div>
  );
}

/**
 * Rückfrage vor dem Rückschreiben.
 *
 * Das Rückschreiben verändert das Zentralmodell und gleicht ab — es ist der
 * einzige Vorgang der Plattform, der nach draußen wirkt und sich nicht ohne
 * Weiteres zurücknehmen lässt. Deshalb eine ausdrückliche Bestätigung, und darin
 * der Name des Modells: Ein Klick auf der falschen Seite soll auffallen, bevor
 * er wirkt.
 */
function Rueckfrage({
  offen, modell, bereich, abbrechen, bestaetigen,
}: {
  offen: boolean;
  modell: string;
  bereich: string;
  abbrechen: () => void;
  bestaetigen: () => void;
}) {
  // Umzug U6c: `ConfirmDialog` der Musterbibliothek. Er bringt mit, was hier
  // von Hand stand (ESC schließt) UND was fehlte: `role="dialog"`,
  // `aria-modal`, Fokus hinein und zurück, Erstfokus auf *Abbrechen*.
  return (
    <ConfirmDialog
      offen={offen}
      art="warnung"
      titel="Modelldaten werden verändert"
      bestaetigenText="Ja, ins Modell schreiben"
      beiBestaetigen={bestaetigen}
      beiAbbrechen={abbrechen}
    >
      <p>
        Die gepflegten Werte aus <strong>{bereich}</strong> werden in das Modell{' '}
        <strong>{modell}</strong> geschrieben und mit der Zentraldatei
        abgeglichen.
      </p>

      <ul style={{ lineHeight: 1.7, paddingLeft: 18 }}>
        <li>Vorhandene Werte im Modell werden <strong>überschrieben</strong>.</li>
        <li>Die Änderung ist für alle sichtbar, die am Modell arbeiten.</li>
        <li>
          Zurücknehmen geht nur im Modell selbst — die Plattform kann es nicht
          rückgängig machen.
        </li>
      </ul>

      <p className="schwach" style={{ fontSize: 12, marginBottom: 0 }}>
        Geschrieben wird nur, wofür ein Zielparameter hinterlegt ist. Fehlt ein
        Parameter im Modell, wird das Feld übersprungen und im Protokoll
        genannt.
      </p>
    </ConfirmDialog>
  );
}
